import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Avatar} from '../../../../../../ui/atoms/avatar';
import {Checkbox} from '../../../../../../ui/atoms/checkbox';
import {MagnifyingGlassIcon} from '../../../../../../ui/atoms/icons';
import {
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  ManagerListFade,
  ManagerRoleChoice,
  ManagerRoleChoiceCopy,
  ManagerRoleChoiceMeta,
  ManagerRoleChoiceName,
  ManagerRoleClearButton,
  ManagerRoleDescription,
  ManagerRoleDialogShell,
  ManagerRoleFooterActions,
  ManagerRoleIntro,
  ManagerRoleNotice,
  ManagerRoleOptions,
  ManagerRoleOptionsViewport,
  ManagerRoleSearch,
  MemberEmpty,
  MemberPrimaryButton,
  SecondaryButton,
} from './member-redesign.styles';
import {getDirectReports, isWorkspaceManager, reviewerId} from './member-role';

export type ManagerRoleChange = {
  kind: 'promote' | 'demote';
  member: any;
};

type ManagerRoleDialogProps = ManagerRoleChange & {
  users: any[];
  managerIds: Set<string>;
  loading?: boolean;
  onClose: () => void;
  onApply: (userIds: string[]) => void;
};

const displayName = (user: any) =>
  user?.firstName && user?.lastName
    ? `${user.firstName} ${user.lastName}`
    : user?.email || 'Unnamed member';

export const ManagerRoleDialog = ({
  kind,
  member,
  users,
  managerIds,
  loading = false,
  onClose,
  onApply,
}: ManagerRoleDialogProps) => {
  const directReports = useMemo(() => getDirectReports(users, member.id), [
    member.id,
    users,
  ]);
  const candidates = useMemo(
    () =>
      kind === 'promote'
        ? users.filter(
            (user) =>
              user.id !== member.id &&
              user.role !== 'admin' &&
              !isWorkspaceManager(user, managerIds),
          )
        : directReports,
    [directReports, kind, managerIds, member.id, users],
  );
  const [selectedIds, setSelectedIds] = useState<string[]>(
    kind === 'demote' ? directReports.map((user) => user.id) : [],
  );
  const [search, setSearch] = useState('');
  const [scrollState, setScrollState] = useState({
    canScrollUp: false,
    canScrollDown: false,
  });
  const optionsRef = useRef<HTMLDivElement>(null);

  const filteredCandidates = candidates.filter((user) =>
    [displayName(user), user.email, user.jobTitle]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(search.toLowerCase())),
  );

  const updateScrollState = () => {
    const options = optionsRef.current;
    if (!options) return;
    setScrollState({
      canScrollUp: options.scrollTop > 2,
      canScrollDown:
        options.scrollTop + options.clientHeight < options.scrollHeight - 2,
    });
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !loading) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [loading, onClose]);

  useEffect(() => {
    optionsRef.current?.scrollTo({top: 0});
    const frame = window.requestAnimationFrame(updateScrollState);
    return () => window.cancelAnimationFrame(frame);
  }, [filteredCandidates.length]);

  const toggleUser = (userId: string) => {
    if (loading) return;
    setSelectedIds((current) =>
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId],
    );
  };

  const canApply =
    kind === 'promote' ? selectedIds.length > 0 : selectedIds.length === 0;
  const actionIds =
    kind === 'promote' ? selectedIds : directReports.map((user) => user.id);

  return (
    <DialogOverlay
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !loading) onClose();
      }}>
      <ManagerRoleDialogShell
        role="dialog"
        aria-modal="true"
        aria-label={
          kind === 'promote' ? 'Promote to manager' : 'Demote to member'
        }>
        <DialogHeader>
          <DialogTitle>
            {kind === 'promote' ? 'Promote to manager' : 'Demote to member'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <ManagerRoleIntro>
            <ManagerRoleDescription>
              {kind === 'promote'
                ? `Choose the people who will report to ${displayName(member)}.`
                : `${displayName(
                    member,
                  )} will become a member after every direct report is cleared.`}
            </ManagerRoleDescription>
            <ManagerRoleNotice>
              {kind === 'promote'
                ? 'Selecting someone who already has a manager will replace their current manager.'
                : 'Clear every selected person below. They will have no manager after this change.'}
            </ManagerRoleNotice>
          </ManagerRoleIntro>

          <ManagerRoleSearch>
            <input
              autoFocus
              aria-label="Search members"
              placeholder="Search members"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <MagnifyingGlassIcon />
          </ManagerRoleSearch>

          <ManagerRoleOptionsViewport>
            {scrollState.canScrollUp && (
              <ManagerListFade position="top" aria-hidden="true" />
            )}
            <ManagerRoleOptions ref={optionsRef} onScroll={updateScrollState}>
              {filteredCandidates.map((user) => {
                const selected = selectedIds.includes(user.id);
                const currentManager = users.find(
                  (candidate) => candidate.id === reviewerId(user.reviewer),
                );
                const meta = currentManager
                  ? `Currently reports to ${displayName(currentManager)}`
                  : user.jobTitle || user.email || 'No manager';
                return (
                  <ManagerRoleChoice
                    key={user.id}
                    htmlFor={`manager-role-member-${user.id}`}
                    $selected={selected}>
                    <Checkbox
                      id={`manager-role-member-${user.id}`}
                      boxSize="small"
                      checked={selected}
                      disabled={loading}
                      aria-label={`Select ${displayName(user)}`}
                      onChange={() => toggleUser(user.id)}
                    />
                    <Avatar
                      name={displayName(user)}
                      userId={user.id}
                      src={user.avatar?.url || ''}
                      size="md"
                      tooltip={false}
                    />
                    <ManagerRoleChoiceCopy>
                      <ManagerRoleChoiceName>
                        {displayName(user)}
                      </ManagerRoleChoiceName>
                      <ManagerRoleChoiceMeta>{meta}</ManagerRoleChoiceMeta>
                    </ManagerRoleChoiceCopy>
                  </ManagerRoleChoice>
                );
              })}
              {!filteredCandidates.length && (
                <MemberEmpty>
                  {search
                    ? 'No members match your search.'
                    : kind === 'promote'
                    ? 'There are no eligible members to assign.'
                    : 'This manager has no direct reports.'}
                </MemberEmpty>
              )}
            </ManagerRoleOptions>
            {scrollState.canScrollDown && (
              <ManagerListFade position="bottom" aria-hidden="true" />
            )}
          </ManagerRoleOptionsViewport>
        </DialogBody>
        <DialogFooter style={{justifyContent: 'space-between'}}>
          <ManagerRoleClearButton
            type="button"
            disabled={!selectedIds.length || loading}
            onClick={() => setSelectedIds([])}>
            Clear all
          </ManagerRoleClearButton>
          <ManagerRoleFooterActions>
            <SecondaryButton type="button" disabled={loading} onClick={onClose}>
              Cancel
            </SecondaryButton>
            <MemberPrimaryButton
              type="button"
              disabled={!canApply || loading}
              onClick={() => onApply(actionIds)}>
              {loading
                ? 'Saving…'
                : kind === 'promote'
                ? 'Promote to manager'
                : 'Demote to member'}
            </MemberPrimaryButton>
          </ManagerRoleFooterActions>
        </DialogFooter>
      </ManagerRoleDialogShell>
    </DialogOverlay>
  );
};
